import {
  assertUploadWithin10Mb,
  uploadSizeLimitApiMessage,
} from "@/constants/uploadLimits";
import { getErrorMessage, isApiError } from "@/utils/errorMessage";
import { ISiteActivationRepository } from "@/repositories/SiteActivationRepository";
import {
  ActivationChecklist,
  SiteReadinessData,
  DocumentReview,
  ActivationWorkflowStatus,
  CreateChecklistTemplateRequest,
  UpdateChecklistTemplateRequest,
  DocumentCategory,
  ChecklistTemplate,
  DashboardResponse,
  DashboardFilters,
  DashboardSummary,
  ActivationSiteSummary,
  AuditHistoryResponse,
  AuditHistoryFilters,
  CreateActivationRequest,
  AddChecklistItemRequest,
  SiteActivationTask,
  AssignableUser,
  CreateTaskRequest,
  UpdateTaskRequest,
  TaskFilters,
  TasksResponse,
  StudyChecklistConfig,
  StudyChecklistConfigItem,
  WaivedGlobalItem,
  CreateStudyChecklistConfigRequest,
  AddStudyConfigItemRequest,
  WaiveGlobalItemRequest,
  ExcludeSiteItemRequest,
  AddSiteExceptionItemRequest,
  ChecklistItem,
  UpdateStudyConfigItemRequest,
  ReverseStudyWaiverRequest,
} from "@/types/siteActivation/siteActivation.type";

export interface ISiteActivationUseCase {
  updateStudyConfigItem(
    studyId: string,
    itemId: string,
    data: UpdateStudyConfigItemRequest,
  ): Promise<StudyChecklistConfigItem>;
  reverseStudyWaiver(
    studyId: string,
    waiverId: string,
    data: ReverseStudyWaiverRequest,
  ): Promise<{ id: string; reversedAt: string; version: number }>;
  getDocumentDownloadUrl(
    studyId: string,
    siteId: string,
    id: string,
  ): Promise<{ url: string; fileName: string }>;
  // Checklist template operations
  createChecklistTemplate(
    data: CreateChecklistTemplateRequest,
  ): Promise<ChecklistTemplate>;
  getTenantChecklistTemplates(): Promise<ChecklistTemplate[]>;
  updateChecklistTemplate(
    id: string,
    data: UpdateChecklistTemplateRequest,
  ): Promise<ChecklistTemplate>;
  archiveChecklistTemplate(id: string): Promise<{ message: string }>;

  // Checklist operations
  getChecklistTemplate(
    studyId: string,
    siteId: string,
  ): Promise<ActivationChecklist>;
  updateChecklistItem(
    studyId: string,
    siteId: string,
    itemId: string,
    data: Partial<ActivationChecklist["items"][0]>,
  ): Promise<ActivationChecklist>;
  submitChecklist(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationChecklist>;

  // Activation creation and item management (simplified flow)
  createActivation(
    studyId: string,
    siteId: string,
    data: CreateActivationRequest,
  ): Promise<ActivationChecklist>;
  addChecklistItem(
    studyId: string,
    siteId: string,
    data: AddChecklistItemRequest,
  ): Promise<ActivationChecklist>;
  removeChecklistItem(
    studyId: string,
    siteId: string,
    itemId: string,
  ): Promise<{ message: string }>;
  submitChecklistDraft(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationChecklist>;

  // Site readiness operations
  getSiteReadiness(studyId: string, siteId: string): Promise<SiteReadinessData>;

  // Document operations
  getDocumentReviews(
    studyId: string,
    siteId: string,
  ): Promise<DocumentReview[]>;
  uploadDocument(
    studyId: string,
    siteId: string,
    documentData: FormData,
  ): Promise<DocumentReview>;
  approveDocument(
    studyId: string,
    siteId: string,
    documentId: string,
  ): Promise<DocumentReview>;
  rejectDocument(
    studyId: string,
    siteId: string,
    documentId: string,
    reason: string,
  ): Promise<DocumentReview>;
  deleteDocument(
    studyId: string,
    siteId: string,
    documentId: string,
  ): Promise<{ message: string }>;

  // Workflow operations
  getActivationStatus(
    studyId: string,
    siteId: string,
  ): Promise<ActivationWorkflowStatus>;

  // PM-specific workflow operations
  startInitiation(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationWorkflowStatus>;
  approveChecklist(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationWorkflowStatus>;
  rejectChecklist(
    studyId: string,
    siteId: string,
    reason: string,
  ): Promise<ActivationWorkflowStatus>;

  // Dashboard operations
  getDashboard(filters?: DashboardFilters): Promise<DashboardResponse>;
  getDashboardSummary(studyId?: string): Promise<DashboardSummary>;
  getSiteSummary(studyId: string): Promise<ActivationSiteSummary>;

  // Export operations
  exportToCSV(filters?: DashboardFilters): Promise<Blob>;

  // Audit history
  getAuditHistory(filters?: AuditHistoryFilters): Promise<AuditHistoryResponse>;

  // Task operations
  getTasks(
    studyId: string,
    siteId: string,
    filters?: TaskFilters,
  ): Promise<TasksResponse>;
  getTask(
    studyId: string,
    siteId: string,
    taskId: string,
  ): Promise<SiteActivationTask>;
  createTask(
    studyId: string,
    siteId: string,
    data: CreateTaskRequest,
  ): Promise<SiteActivationTask>;
  updateTask(
    studyId: string,
    siteId: string,
    taskId: string,
    data: UpdateTaskRequest,
  ): Promise<SiteActivationTask>;
  deleteTask(
    studyId: string,
    siteId: string,
    taskId: string,
  ): Promise<{ message: string }>;
  getAssignableUsers(
    studyId: string,
    siteId: string,
  ): Promise<AssignableUser[]>;

  // Study Checklist Configuration operations
  getStudyChecklistConfig(studyId: string): Promise<StudyChecklistConfig>;
  createStudyChecklistConfig(
    studyId: string,
    data: CreateStudyChecklistConfigRequest,
  ): Promise<StudyChecklistConfig>;
  addStudyConfigItem(
    studyId: string,
    data: AddStudyConfigItemRequest,
  ): Promise<StudyChecklistConfigItem>;
  waiveGlobalItem(
    studyId: string,
    data: WaiveGlobalItemRequest,
  ): Promise<WaivedGlobalItem>;

  // Site Item Exclusion and Exception operations
  excludeSiteItem(
    studyId: string,
    siteId: string,
    itemId: string,
    data: ExcludeSiteItemRequest,
  ): Promise<ChecklistItem>;
  addSiteExceptionItem(
    studyId: string,
    siteId: string,
    data: AddSiteExceptionItemRequest,
  ): Promise<ChecklistItem>;
}

export class SiteActivationUseCase implements ISiteActivationUseCase {
  constructor(private repository: ISiteActivationRepository) {}

  async createChecklistTemplate(
    data: CreateChecklistTemplateRequest,
  ): Promise<ChecklistTemplate> {
    this.validateTemplate(data);
    return this.repository.createChecklistTemplate(data);
  }

  getTenantChecklistTemplates(): Promise<ChecklistTemplate[]> {
    return this.repository.getTenantChecklistTemplates();
  }

  async updateChecklistTemplate(
    id: string,
    data: UpdateChecklistTemplateRequest,
  ): Promise<ChecklistTemplate> {
    if (!id.trim()) {
      throw new Error("Template ID is required");
    }
    this.validateTemplate(data);
    return this.repository.updateChecklistTemplate(id, data);
  }

  async archiveChecklistTemplate(id: string): Promise<{ message: string }> {
    if (!id.trim()) {
      throw new Error("Template ID is required");
    }
    return this.repository.archiveChecklistTemplate(id);
  }

  private validateTemplate(data: UpdateChecklistTemplateRequest): void {
    if (!data.name?.trim()) {
      throw new Error("Template name is required");
    }
    if (data.name.trim().length < 3 || data.name.length > 255) {
      throw new Error("Template name must be between 3 and 255 characters");
    }
    if (data.description.length > 2000) {
      throw new Error("Template description must not exceed 2000 characters");
    }
    if (!data.items || data.items.length === 0) {
      throw new Error("At least one checklist item is required");
    }

    data.items.forEach((item, index) => {
      if (!item.title?.trim()) {
        throw new Error(`Item ${index + 1}: Title is required`);
      }
      if (item.title.length > 255 || item.description.length > 2000) {
        throw new Error(`Item ${index + 1}: Title or description is too long`);
      }
      if (
        !Object.values(DocumentCategory).includes(
          item.category as DocumentCategory,
        )
      ) {
        throw new Error(`Item ${index + 1}: A valid category is required`);
      }
      if (
        typeof item.isMandatory !== "boolean" ||
        typeof item.requiresEvidence !== "boolean" ||
        !Number.isInteger(item.sortOrder) ||
        item.sortOrder < 0
      ) {
        throw new Error(`Item ${index + 1}: Invalid checklist settings`);
      }
    });
  }

  async getChecklistTemplate(
    studyId: string,
    siteId: string,
  ): Promise<ActivationChecklist> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    try {
      return await this.repository.getChecklistTemplate(studyId, siteId);
    } catch (error) {
      throw new Error(
        `Failed to fetch checklist template: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async updateChecklistItem(
    studyId: string,
    siteId: string,
    itemId: string,
    data: Partial<ActivationChecklist["items"][0]>,
  ): Promise<ActivationChecklist> {
    if (!studyId || !siteId || !itemId) {
      throw new Error("Study ID, Site ID, and Item ID are required");
    }

    try {
      return await this.repository.updateChecklistItem(
        studyId,
        siteId,
        itemId,
        data,
      );
    } catch (error) {
      throw new Error(
        `Failed to update checklist item: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async submitChecklist(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationChecklist> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    try {
      return await this.repository.submitChecklist(studyId, siteId, notes);
    } catch (error) {
      throw new Error(
        `Failed to submit checklist: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async createActivation(
    studyId: string,
    siteId: string,
    data: CreateActivationRequest,
  ): Promise<ActivationChecklist> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    try {
      return await this.repository.createActivation(studyId, siteId, data);
    } catch (error) {
      throw new Error(
        `Failed to create activation: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async addChecklistItem(
    studyId: string,
    siteId: string,
    data: AddChecklistItemRequest,
  ): Promise<ActivationChecklist> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }
    if (!data.title?.trim()) {
      throw new Error("Checklist item title is required");
    }
    if (!data.category) {
      throw new Error("Checklist item category is required");
    }

    try {
      return await this.repository.addChecklistItem(studyId, siteId, data);
    } catch (error) {
      throw new Error(
        `Failed to add checklist item: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async removeChecklistItem(
    studyId: string,
    siteId: string,
    itemId: string,
  ): Promise<{ message: string }> {
    if (!studyId || !siteId || !itemId) {
      throw new Error("Study ID, Site ID, and Item ID are required");
    }

    try {
      return await this.repository.removeChecklistItem(studyId, siteId, itemId);
    } catch (error) {
      throw new Error(
        `Failed to remove checklist item: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async submitChecklistDraft(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationChecklist> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    try {
      return await this.repository.submitChecklistDraft(studyId, siteId, notes);
    } catch (error) {
      throw new Error(
        `Failed to submit checklist draft: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async getSiteReadiness(
    studyId: string,
    siteId: string,
  ): Promise<SiteReadinessData> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    try {
      return await this.repository.getSiteReadiness(studyId, siteId);
    } catch (error) {
      throw new Error(
        `Failed to fetch site readiness: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async getDocumentReviews(
    studyId: string,
    siteId: string,
  ): Promise<DocumentReview[]> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    try {
      return await this.repository.getDocumentReviews(studyId, siteId);
    } catch (error) {
      throw new Error(
        `Failed to fetch document reviews: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async uploadDocument(
    studyId: string,
    siteId: string,
    documentData: FormData,
  ): Promise<DocumentReview> {
    if (!studyId || !siteId || !documentData) {
      throw new Error("Study ID, Site ID, and Document data are required");
    }

    const fileEntry = documentData.get("file");
    if (fileEntry instanceof File) {
      assertUploadWithin10Mb(fileEntry);
    }

    try {
      return await this.repository.uploadDocument(
        studyId,
        siteId,
        documentData,
      );
    } catch (error) {
      const sizeMessage = uploadSizeLimitApiMessage(error);
      throw new Error(
        sizeMessage ?? getErrorMessage(error, "Failed to upload document"),
      );
    }
  }

  async approveDocument(
    studyId: string,
    siteId: string,
    documentId: string,
  ): Promise<DocumentReview> {
    if (!studyId || !siteId || !documentId) {
      throw new Error("Study ID, Site ID, and Document ID are required");
    }

    try {
      return await this.repository.approveDocument(studyId, siteId, documentId);
    } catch (error) {
      throw new Error(
        `Failed to approve document: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async rejectDocument(
    studyId: string,
    siteId: string,
    documentId: string,
    reason: string,
  ): Promise<DocumentReview> {
    if (!studyId || !siteId || !documentId || !reason) {
      throw new Error(
        "Study ID, Site ID, Document ID, and Reason are required",
      );
    }

    try {
      return await this.repository.rejectDocument(
        studyId,
        siteId,
        documentId,
        reason,
      );
    } catch (error) {
      throw new Error(
        `Failed to reject document: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async deleteDocument(
    studyId: string,
    siteId: string,
    documentId: string,
  ): Promise<{ message: string }> {
    if (!studyId || !siteId || !documentId) {
      throw new Error("Study ID, Site ID, and Document ID are required");
    }

    try {
      return await this.repository.deleteDocument(studyId, siteId, documentId);
    } catch (error) {
      throw new Error(
        `Failed to delete document: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async getDocumentDownloadUrl(
    studyId: string,
    siteId: string,
    documentId: string,
  ): Promise<{ url: string; fileName: string }> {
    if (!studyId || !siteId || !documentId) {
      throw new Error("Study ID, Site ID, and Document ID are required");
    }

    try {
      return await this.repository.getDocumentDownloadUrl(
        studyId,
        siteId,
        documentId,
      );
    } catch (error) {
      throw new Error(
        `Failed to get document download URL: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async getActivationStatus(
    studyId: string,
    siteId: string,
  ): Promise<ActivationWorkflowStatus> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    try {
      return await this.repository.getActivationStatus(studyId, siteId);
    } catch (error) {
      if (isApiError(error)) {
        throw error;
      }
      throw new Error(
        `Failed to fetch activation status: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  // PM-specific workflow operations
  async startInitiation(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationWorkflowStatus> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    try {
      return await this.repository.startInitiation(studyId, siteId, notes);
    } catch (error) {
      throw new Error(
        `Failed to start initiation: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async approveChecklist(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationWorkflowStatus> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    try {
      return await this.repository.approveChecklist(studyId, siteId, notes);
    } catch (error) {
      throw new Error(
        `Failed to approve checklist: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async rejectChecklist(
    studyId: string,
    siteId: string,
    reason: string,
  ): Promise<ActivationWorkflowStatus> {
    if (!studyId || !siteId || !reason) {
      throw new Error("Study ID, Site ID, and Reason are required");
    }

    try {
      return await this.repository.rejectChecklist(studyId, siteId, reason);
    } catch (error) {
      throw new Error(
        `Failed to reject checklist: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  // Dashboard operations
  async getDashboard(filters?: DashboardFilters): Promise<DashboardResponse> {
    try {
      return await this.repository.getDashboard(filters);
    } catch (error) {
      throw new Error(
        `Failed to fetch dashboard: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async getDashboardSummary(studyId?: string): Promise<DashboardSummary> {
    try {
      return await this.repository.getDashboardSummary(studyId);
    } catch (error) {
      throw new Error(
        `Failed to fetch dashboard summary: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async getSiteSummary(studyId: string): Promise<ActivationSiteSummary> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    return this.repository.getSiteSummary(studyId);
  }

  // Export operations
  async exportToCSV(filters?: DashboardFilters): Promise<Blob> {
    try {
      return await this.repository.exportToCSV(filters);
    } catch (error) {
      throw new Error(
        `Failed to export CSV: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  // Audit history
  async getAuditHistory(
    filters?: AuditHistoryFilters,
  ): Promise<AuditHistoryResponse> {
    try {
      return await this.repository.getAuditHistory(filters);
    } catch (error) {
      throw new Error(
        `Failed to fetch audit history: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  // Task operations
  async getTasks(
    studyId: string,
    siteId: string,
    filters?: TaskFilters,
  ): Promise<TasksResponse> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    try {
      return await this.repository.getTasks(studyId, siteId, filters);
    } catch (error) {
      throw new Error(
        `Failed to fetch tasks: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async getTask(
    studyId: string,
    siteId: string,
    taskId: string,
  ): Promise<SiteActivationTask> {
    if (!studyId || !siteId || !taskId) {
      throw new Error("Study ID, Site ID, and Task ID are required");
    }

    try {
      return await this.repository.getTask(studyId, siteId, taskId);
    } catch (error) {
      throw new Error(
        `Failed to fetch task: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async createTask(
    studyId: string,
    siteId: string,
    data: CreateTaskRequest,
  ): Promise<SiteActivationTask> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    if (
      !data.taskTitle ||
      !data.description ||
      !data.assignedToId ||
      !data.dueDate ||
      !data.priority
    ) {
      throw new Error("All required task fields must be provided");
    }

    try {
      return await this.repository.createTask(studyId, siteId, data);
    } catch (error) {
      throw new Error(
        `Failed to create task: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async updateTask(
    studyId: string,
    siteId: string,
    taskId: string,
    data: UpdateTaskRequest,
  ): Promise<SiteActivationTask> {
    if (!studyId || !siteId || !taskId) {
      throw new Error("Study ID, Site ID, and Task ID are required");
    }

    try {
      return await this.repository.updateTask(studyId, siteId, taskId, data);
    } catch (error) {
      throw new Error(
        `Failed to update task: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async deleteTask(
    studyId: string,
    siteId: string,
    taskId: string,
  ): Promise<{ message: string }> {
    if (!studyId || !siteId || !taskId) {
      throw new Error("Study ID, Site ID, and Task ID are required");
    }

    try {
      return await this.repository.deleteTask(studyId, siteId, taskId);
    } catch (error) {
      throw new Error(
        `Failed to delete task: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async getAssignableUsers(
    studyId: string,
    siteId: string,
  ): Promise<AssignableUser[]> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    try {
      return await this.repository.getAssignableUsers(studyId, siteId);
    } catch (error) {
      throw new Error(
        `Failed to fetch assignable users: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async getStudyChecklistConfig(
    studyId: string,
  ): Promise<StudyChecklistConfig> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    return this.repository.getStudyChecklistConfig(studyId);
  }

  async createStudyChecklistConfig(
    studyId: string,
    data: CreateStudyChecklistConfigRequest,
  ): Promise<StudyChecklistConfig> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    if (!data.templateId) {
      throw new Error("A baseline template is required");
    }
    try {
      return await this.repository.createStudyChecklistConfig(studyId, data);
    } catch (error) {
      throw new Error(
        `Failed to create study checklist config: ${getErrorMessage(error, "Unknown error")}`,
      );
    }
  }

  async addStudyConfigItem(
    studyId: string,
    data: AddStudyConfigItemRequest,
  ): Promise<StudyChecklistConfigItem> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    this.validateStudyItem(data);
    try {
      return await this.repository.addStudyConfigItem(studyId, data);
    } catch (error) {
      throw new Error(
        `Failed to add study config item: ${getErrorMessage(error, "Unknown error")}`,
      );
    }
  }

  async waiveGlobalItem(
    studyId: string,
    data: WaiveGlobalItemRequest,
  ): Promise<WaivedGlobalItem> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    if (!data.globalTemplateItemId || !data.reason.trim()) {
      throw new Error("Item and waiver reason are required");
    }
    try {
      return await this.repository.waiveGlobalItem(studyId, data);
    } catch (error) {
      throw new Error(
        `Failed to waive global item: ${getErrorMessage(error, "Unknown error")}`,
      );
    }
  }

  async excludeSiteItem(
    studyId: string,
    siteId: string,
    itemId: string,
    data: ExcludeSiteItemRequest,
  ): Promise<ChecklistItem> {
    if (!studyId || !siteId || !itemId) {
      throw new Error("Study ID, Site ID, and Item ID are required");
    }
    try {
      return await this.repository.excludeSiteItem(
        studyId,
        siteId,
        itemId,
        data,
      );
    } catch (error) {
      throw new Error(
        `Failed to exclude checklist item: ${getErrorMessage(error, "Unknown error")}`,
      );
    }
  }

  async addSiteExceptionItem(
    studyId: string,
    siteId: string,
    data: AddSiteExceptionItemRequest,
  ): Promise<ChecklistItem> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }
    try {
      return await this.repository.addSiteExceptionItem(studyId, siteId, data);
    } catch (error) {
      throw new Error(
        `Failed to add site exception item: ${getErrorMessage(error, "Unknown error")}`,
      );
    }
  }

  async updateStudyConfigItem(
    studyId: string,
    itemId: string,
    data: UpdateStudyConfigItemRequest,
  ): Promise<StudyChecklistConfigItem> {
    if (!studyId || !itemId) {
      throw new Error("Study ID and Item ID are required");
    }
    this.validateStudyItem(data);
    this.validateConfigChange(data);
    return this.repository.updateStudyConfigItem(studyId, itemId, data);
  }

  async reverseStudyWaiver(
    studyId: string,
    waiverId: string,
    data: ReverseStudyWaiverRequest,
  ): Promise<{ id: string; reversedAt: string; version: number }> {
    if (!studyId || !waiverId) {
      throw new Error("Study ID and Waiver ID are required");
    }
    this.validateConfigChange(data);
    return this.repository.reverseStudyWaiver(studyId, waiverId, data);
  }

  private validateConfigChange(data: ReverseStudyWaiverRequest): void {
    if (
      !data.reason.trim() ||
      data.reason.length > 2000 ||
      !Number.isInteger(data.expectedVersion) ||
      data.expectedVersion < 1
    ) {
      throw new Error(
        "A reason and current configuration version are required",
      );
    }
  }

  private validateStudyItem(data: AddStudyConfigItemRequest): void {
    if (
      !data.title.trim() ||
      data.title.length > 255 ||
      (data.description?.length ?? 0) > 2000 ||
      !Object.values(DocumentCategory).includes(
        data.category as DocumentCategory,
      )
    ) {
      throw new Error(
        "A valid item title, category and description are required",
      );
    }
  }
}
